/**
 * Notification Template Constants
 *
 * Extension URLs, code systems, and static config for the Notifications feature.
 */
import type {
  NotificationTemplateDraft,
  NotificationSenderConfig,
} from './notification-template.types';

// ---------------------------------------------------------------------------
// Extension URLs
// ---------------------------------------------------------------------------

const NOTIFICATION_EXT_BASE =
  'https://unify.health/fhir/StructureDefinition';

/** Extension on ActivityDefinition carrying the full template JSON config. */
export const NOTIFICATION_TEMPLATE_CONFIG_EXTENSION =
  `${NOTIFICATION_EXT_BASE}/notification-template-config` as const;

/** Identifier system for notification dedupe keys on CommunicationRequest / Communication. */
export const NOTIFICATION_DEDUPE_KEY_SYSTEM =
  'https://unifymentalhealth.com/notification/dedupe-key' as const;

/** Code system for notification audit events. */
export const NOTIFICATION_AUDIT_CODE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/notification-engine' as const;

/** Canonical URL prefix for notification template ActivityDefinitions. */
export const NOTIFICATION_AD_URL_PREFIX =
  'https://unify.health/fhir/ActivityDefinition/notification-template/' as const;

// ---------------------------------------------------------------------------
// Medium coding (email)
// ---------------------------------------------------------------------------

export const EMAIL_MEDIUM_CODING = {
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
  code: 'EMAILWRIT',
  display: 'email',
} as const;

// ---------------------------------------------------------------------------
// Status mapping: app status ↔ FHIR ActivityDefinition.status
// ---------------------------------------------------------------------------

export const STATUS_TO_FHIR: Record<string, 'draft' | 'active' | 'retired'> = {
  draft: 'draft',
  active: 'active',
  archived: 'retired',
};

export const STATUS_FROM_FHIR: Record<string, 'draft' | 'active' | 'archived'> = {
  draft: 'draft',
  active: 'active',
  retired: 'archived',
};

// ---------------------------------------------------------------------------
// UI labels & options
// ---------------------------------------------------------------------------

export const TEMPLATE_STATUS_OPTIONS: { value: string; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: 'yellow' },
  { value: 'active', label: 'Active', color: 'green' },
  { value: 'archived', label: 'Archived', color: 'gray' },
];

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

export const DEFAULT_SENDER: NotificationSenderConfig = {
  mode: 'static',
  email: '',
  name: '',
};

export const STARTER_HTML_BODY = `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif">
  <tr>
    <td style="padding:32px">
      <p style="margin:0 0 16px;font-size:16px;color:#333">Hi {{patient.firstName}},</p>
      <p style="margin:0 0 16px;font-size:15px;color:#555">Your content goes here. Edit this template to compose your email.</p>
      <p style="margin:0 0 16px;font-size:15px;color:#555">Best regards</p>
    </td>
  </tr>
  <tr>
    <td style="padding:16px 32px;border-top:1px solid #eee">
      <p style="margin:0;font-size:12px;color:#999;text-align:center">Unify Mental Health</p>
    </td>
  </tr>
</table>`;

export const DEFAULT_TEMPLATE_DRAFT: NotificationTemplateDraft = {
  name: '',
  description: '',
  status: 'draft',
  version: '1',
  channel: 'email',
  from: { ...DEFAULT_SENDER },
  subject: '',
  htmlBody: STARTER_HTML_BODY,
  textBody: '',
  variables: [],
};

// ---------------------------------------------------------------------------
// Email validation regex
// ---------------------------------------------------------------------------

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
